import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, HandCoins, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OweButton({ booking, token, onSaved }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (notify) => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter an owed amount greater than $0."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/manual-owe`,
        { amount: amt, reason, notify },
        { headers: { Authorization: `Bearer ${token}` } });
      if (notify && data.channels.length === 0) {
        toast.warning(`$${data.added} added (balance $${data.balance.toFixed(2)}) — but the notice could not be sent by text or email.`);
      } else if (notify) {
        toast.success(`$${data.added} added & sent by ${data.channels.join(" + ")} — balance now $${data.balance.toFixed(2)}.`);
      } else {
        toast.success(`$${data.added} added — balance now $${data.balance.toFixed(2)}.`);
      }
      setOpen(false); setAmount(""); setReason("");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add owed amount.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button data-testid={`owe-btn-${booking.id}`} onClick={() => setOpen(true)}
        className="text-xs flex items-center gap-1.5 border border-amber-500/40 text-amber-400 px-3 py-1.5 rounded hover:bg-amber-500 hover:text-black transition-colors">
        <HandCoins size={13} /> Owe
      </button>
    );
  }
  return (
    <div data-testid={`owe-form-${booking.id}`} className="w-full mt-2 rounded-lg border border-amber-500/30 bg-[#0A0A0A] p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-amber-400 flex items-center gap-1.5"><HandCoins size={14} /> Renter Owes</span>
        <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white"><X size={16} /></button>
      </div>
      <div className="flex gap-2">
        <input data-testid={`owe-amount-${booking.id}`} type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount $"
          className="w-28 bg-[#0C0C0C] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-500/50" />
        <input data-testid={`owe-reason-${booking.id}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional — gas, tolls, late fee...)"
          className="flex-1 bg-[#0C0C0C] border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-500/50" />
      </div>
      <div className="flex gap-2">
        <button data-testid={`owe-add-${booking.id}`} onClick={() => submit(false)} disabled={busy}
          className="flex-1 border border-amber-500/50 text-amber-300 font-semibold text-sm px-3 py-2 rounded hover:bg-amber-500/10 transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5">
          {busy && <Loader2 className="animate-spin" size={14} />} Add to Balance
        </button>
        <button data-testid={`owe-add-send-${booking.id}`} onClick={() => submit(true)} disabled={busy}
          className="flex-1 bg-amber-500 text-black font-bold text-sm px-3 py-2 rounded hover:bg-amber-400 transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5">
          {busy && <Loader2 className="animate-spin" size={14} />} Add & Text Amount
        </button>
      </div>
      <p className="text-[11px] text-white/35">"Add & Text Amount" texts + emails the renter the amount owed with a secure pay link. You can also use Send Payment Reminder later to nudge them.</p>
    </div>
  );
}
